import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Loader2, Clock, Bot, Film, CheckCircle, XCircle } from 'lucide-react';

const API = window.__API_BASE + '/api';

const STATUS_CONFIG = {
  pending:    { icon: Clock,       label: 'Queued — Preparing your video', color: 'text-gray-400',   pct: 5 },
  claimed:    { icon: Bot,         label: 'Picked up — Generation starting', color: 'text-yellow-400', pct: 15 },
  processing: { icon: Film,        label: 'Generating Montage Video...', color: 'text-teal-400',  pct: 60 },
  completed:  { icon: CheckCircle, label: 'Video Ready', color: 'text-green-400', pct: 100 },
  failed:     { icon: XCircle,     label: 'Failed', color: 'text-red-400',   pct: 0 },
};

export default function SeedanceJobStatus({ jobId, onVideoReady }) {
  const [job, setJob] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => {
    if (!jobId) return;

    const poll = async () => {
      try {
        const res = await axios.get(`${API}/seedance/job-status/${jobId}`);
        setJob(res.data);
        if (res.data.status === 'completed') {
          onVideoReady?.(res.data.output?.video_url);
          clearInterval(pollRef.current);
        } else if (res.data.status === 'failed') {
          clearInterval(pollRef.current);
        }
      } catch { /* silent */ }
    };

    poll();
    pollRef.current = setInterval(poll, 5000);
    return () => clearInterval(pollRef.current);
  }, [jobId, onVideoReady]);

  if (!job) return null;

  const config = STATUS_CONFIG[job.status] || STATUS_CONFIG.pending;
  const Icon = config.icon;

  return (
    <div className="mt-3 p-3 rounded-lg border border-white/10" style={{ background: 'rgba(6,18,28,0.6)' }} data-testid="seedance-job-status">
      <div className="flex items-center gap-2 mb-2">
        {job.status === 'processing' ? (
          <Loader2 className={`w-4 h-4 ${config.color} animate-spin`} />
        ) : (
          <Icon className={`w-4 h-4 ${config.color}`} />
        )}
        <span className={`text-sm font-medium ${config.color}`}>
          {config.label}
        </span>
      </div>

      {/* Progress bar */}
      {job.status !== 'failed' && (
        <div className="w-full bg-white/5 rounded-full h-1.5 mb-2">
          <div
            className="h-1.5 rounded-full transition-all duration-500"
            style={{ width: `${config.pct}%`, background: 'linear-gradient(90deg, #22D3EE, #14B8A6)' }}
          />
        </div>
      )}

      {/* Status note */}
      {job.status_note && (
        <p className="text-xs text-white/50 mt-1">{job.status_note}</p>
      )}

      {/* Error message */}
      {job.status === 'failed' && job.error && (
        <p className="text-xs text-red-400 mt-1">{job.error}</p>
      )}

      {/* Video player on completion */}
      {job.status === 'completed' && job.output?.video_url && (
        <video
          src={job.output.video_url}
          controls
          className="w-full rounded-lg mt-2"
          style={{ maxHeight: '300px' }}
          data-testid="seedance-video-player"
        />
      )}

      <div className="flex items-center gap-1 mt-2">
        <span className="text-[9px] text-white/30 px-2 py-0.5 rounded-full border border-white/10">
          Powered by EiPi Montage Engine
        </span>
      </div>
    </div>
  );
}
